import { create } from 'zustand'
import { auth, db } from '../services/firebase'
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot, collection, addDoc } from 'firebase/firestore'

export const useAuthStore = create((set) => ({
  user: null,
  loading: false,
  error: null,

  login: async (email, password) => {
    set({ loading: true, error: null })
    try {
      if (!auth) throw new Error('Firebase no está configurado')
      const cred = await signInWithEmailAndPassword(auth, email, password)
      const userDoc = await getDoc(doc(db, 'users', cred.user.uid))
      const data = userDoc.exists() ? userDoc.data() : {}
      const role = data.role || 'user'
      const maxCards = typeof data.maxCards === 'number' ? data.maxCards : 1
      const isActive = data.active === true
      const envAdminEmail = (import.meta.env.VITE_ADMIN_EMAIL || '').trim().toLowerCase()
      const isEnvAdmin = envAdminEmail && cred.user.email?.toLowerCase() === envAdminEmail
      if (isEnvAdmin && role !== 'admin') {
        try {
          await setDoc(doc(db, 'users', cred.user.uid), { role: 'admin', active: true }, { merge: true })
        } catch {}
      }
      const isAdmin = (role === 'admin' || isEnvAdmin)
      if (!isAdmin && !isActive) {
        await signOut(auth)
        throw new Error('Tu cuenta está pendiente de aprobación. Intenta más tarde.')
      }
      set({ user: { uid: cred.user.uid, email: cred.user.email, displayName: cred.user.displayName, role, maxCards }, loading: false })
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  register: async (email, password, displayName) => {
    set({ loading: true, error: null })
    try {
      if (!auth) throw new Error('Firebase no está configurado')
      const cred = await createUserWithEmailAndPassword(auth, email, password)
      const payload = {
        uid: cred.user.uid,
        displayName: displayName || email,
        email,
        role: 'user',
        plan: 'free',
        maxCards: 1,
        active: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }
      const withTimeout = (promise, ms) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))
        ])
      }
      try {
        await withTimeout(setDoc(doc(db, 'users', cred.user.uid), payload, { merge: true }), 7000)
      } catch (e) {
        try { await setDoc(doc(db, 'users', cred.user.uid), payload, { merge: true }) } catch {}
      }
      const created = await getDoc(doc(db, 'users', cred.user.uid))
      if (!created.exists()) {
        await setDoc(doc(db, 'users', cred.user.uid), payload, { merge: true })
        try {
          await addDoc(collection(db, 'contacts'), { name: displayName || email, email, subject: 'Registro (retry users)', message: 'Se creó el usuario tras reintento', status: 'new', createdAt: serverTimestamp() })
        } catch {}
      }
      await signOut(auth)
      set({ user: null, loading: false })
      return { pending: true }
    } catch (error) {
      try {
        await addDoc(collection(db, 'contacts'), { name: displayName || email, email, subject: 'Registro fallido', message: error?.message || 'Error al crear usuario', status: 'new', createdAt: serverTimestamp() })
      } catch {}
      set({ error: error.message, loading: false })
      throw error
    }
  },

  logout: async () => {
    set({ loading: true })
    try {
      if (!auth) {
        set({ user: null, loading: false })
        return
      }
      await signOut(auth)
      set({ user: null, loading: false })
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  checkAuth: () => {
    if (!auth) {
      return () => {}
    }
    let userDocUnsub = null
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        const userDoc = await getDoc(doc(db, 'users', fbUser.uid))
        const data = userDoc.exists() ? userDoc.data() : {}
        const role = data.role || 'user'
        const maxCards = typeof data.maxCards === 'number' ? data.maxCards : 1
        const isActive = data.active === true
        const envAdminEmail = (import.meta.env.VITE_ADMIN_EMAIL || '').trim().toLowerCase()
        const isEnvAdmin = envAdminEmail && fbUser.email?.toLowerCase() === envAdminEmail
        if (isEnvAdmin && role !== 'admin') {
          try {
            await setDoc(doc(db, 'users', fbUser.uid), { role: 'admin', active: true }, { merge: true })
          } catch {}
        }
        const isAdmin = (role === 'admin' || isEnvAdmin)
        if (!isAdmin && !isActive) {
          await signOut(auth)
          set({ user: null })
          return
        }
        set({ user: { uid: fbUser.uid, email: fbUser.email, displayName: fbUser.displayName, role, maxCards } })

        // Subscribe to real-time changes on user doc (role, maxCards, active)
        if (userDocUnsub) userDocUnsub()
        userDocUnsub = onSnapshot(doc(db, 'users', fbUser.uid), (snap) => {
          const d = snap.exists() ? snap.data() : {}
          const updatedRole = d.role || 'user'
          const updatedMaxCards = typeof d.maxCards === 'number' ? d.maxCards : 1
          const envAdminEmail2 = (import.meta.env.VITE_ADMIN_EMAIL || '').trim().toLowerCase()
          const isEnvAdmin2 = envAdminEmail2 && fbUser.email?.toLowerCase() === envAdminEmail2
          const finalRole = (updatedRole === 'admin' || isEnvAdmin2) ? 'admin' : updatedRole
          set({ user: { uid: fbUser.uid, email: fbUser.email, displayName: fbUser.displayName, role: finalRole, maxCards: updatedMaxCards } })
        })
      } else {
        set({ user: null })
        if (userDocUnsub) { userDocUnsub(); userDocUnsub = null }
      }
    })
    return () => { if (userDocUnsub) userDocUnsub(); unsubscribe() }
  },

  clearError: () => set({ error: null })
}))
